import { useEffect, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Chip, Icon, Modal, StatCard, ToggleRow } from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PatientDisclosureModal } from '@/components/audioRetention/PatientDisclosureCard';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { SettingsUnreadable } from './Unreadable';
import { usePrivacyPolicy, useSavePrivacyPolicy } from './useSettings';
import { ExportEverything } from './ExportEverything';

/**
 * Clinic manager · Settings ▸ Privacy — `g_set_priv.dc.html`.
 *
 * THE WINDOW IS LIVE, which is the board's own subtitle. The four chips are
 * the retention window, and the four counters under them are what that window
 * has already produced: `active` is inside it, `purged` is what fell out of
 * it, `promoted` is what was lifted out of it into the training corpus and
 * `excluded` is what consent took back out. They come from the audio records'
 * own lifecycle column, not from a statistic somebody kept.
 *
 * ONE ACT, ONE WRITE. `setOrgConsent` takes the switch AND the window together
 * and stamps a consent audit row with the actor, the reason and the IP — so
 * Save sends both, always, and the screen never half-applies a privacy change.
 *
 * THE THREE BUTTONS ARE NOT THREE OF A KIND.
 *   • Patient disclosure is real and always was: the bilingual notice the
 *     encounter screen already prints, opened here with THIS clinic's window
 *     in it.
 *   • Export everything is the board's own admission — "the promise, not a
 *     feature". Nothing in the product assembles a whole-clinic zip; the
 *     nearest thing is a per-patient GDPR export behind `admin:read`. So the
 *     control says so instead of starting a job that does not exist.
 *   • Purge all retained audio is real, irreversible and gated on
 *     `system:admin` — a platform permission no clinic role holds. It is
 *     marked as the refusal it would be, rather than firing a destructive
 *     call to find out (§4.8).
 */

const WINDOWS = [30, 60, 90, 180];

export function ManagerPrivacyPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const { value, isLoading, error, refetch } = usePrivacyPolicy();
  const save = useSavePrivacyPolicy();
  const [draft, setDraft] = useState<{ enabled: boolean; retentionDays: number } | null>(null);
  const [query, setQuery] = useState('');
  const [disclosure, setDisclosure] = useState(false);
  const [explaining, setExplaining] = useState<'purge' | null>(null);
  const [exporting, setExporting] = useState(false);

  usePageMeta({ title: 'Privacy · System Settings' });

  useEffect(() => {
    if (value && !draft) setDraft({ enabled: value.orgConsent, retentionDays: value.retentionDays });
  }, [value, draft]);

  const shown = draft ?? (value ? { enabled: value.orgConsent, retentionDays: value.retentionDays } : null);
  const dirty = !!draft && !!value
    && (draft.enabled !== value.orgConsent || draft.retentionDays !== value.retentionDays);
  const reading = isLoading && !value;
  const unreadable = !reading && !value;

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const showConsent = matches('contribute consultation audio for AI training');
  const showWindow = matches('retain audio for');
  const showCounts = matches('what is on file right now');
  const anyShown = showConsent || showWindow || showCounts;

  const crumb = usePhoneCrumb('Settings', 'Privacy');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Privacy',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Privacy
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="privacy" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft ? {
        label: save.isPending ? 'Saving…' : 'Save',
        onClick: () => save.mutate(draft),
      } : undefined,
    },
    [query, dirty, save.isPending, !!draft, unreadable],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s privacy policy…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="privacy policy" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {value && shown ? (
          <>
            {showConsent ? (
              <>
                {narrow ? null : <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
                  Audio &amp; AI training — organisation-wide
                </p>}
                <ToggleRow
                  label="Contribute consultation audio for AI training"
                  captionOn="encrypted with your organisation's key · the consent is the doctor's, stamped at ambient start"
                  captionOff="encrypted with your organisation's key · the consent is the doctor's, stamped at ambient start"
                  checked={shown.enabled}
                  onChange={(next) => setDraft({ ...shown, enabled: next })}
                />
              </>
            ) : null}

            {showWindow ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Retain audio for</p>
                <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {WINDOWS.map((d) => (
                    <Chip key={d}
                      kind={d === shown.retentionDays ? 'brand' : 'outline'} small
                      className={d === shown.retentionDays ? 'w6' : undefined}
                      onClick={() => setDraft({ ...shown, retentionDays: d })}>
                      {d} d
                    </Chip>
                  ))}
                  {/* A clinic whose window was set to something else — the
                      server takes anything in [1, 365] — keeps its own chip
                      rather than being shown four that are all unselected. */}
                  {WINDOWS.includes(shown.retentionDays) ? null : (
                    <Chip kind="brand" small className="w6">{shown.retentionDays} d</Chip>
                  )}
                </div>
                {narrow ? null : <p className="t-cap mut" style={{ marginTop: 6 }}>
                  auto-purged after the window unless it was promoted to training
                </p>}
              </>
            ) : null}

            {showCounts ? (
              <>
                {narrow ? null : <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>What is on file right now</p>}
                <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 16px' }}>
                  <StatCard label="Active" value={value.counts.active} />
                  <StatCard label="In training" value={value.counts.promoted} />
                  <StatCard label="Purged" value={value.counts.purged} />
                  {narrow ? null : <StatCard label="Excluded" value={value.counts.excluded} />}
                </div>
              </>
            ) : null}

            {!anyShown ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No setting on this screen matches that. Clear the search to see the policy again.
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-outline" onClick={() => setDisclosure(true)}>
                <Icon name="file-text" size="sm" /> Patient disclosure
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setExporting(true)}>
                <Icon name="download" size="sm" /> Export everything
              </button>
              <button type="button" className="btn cm-purge"
                style={{ background: 'var(--danger-100)', color: 'var(--danger-600)', marginLeft: 'auto' }}
                onClick={() => setExplaining('purge')}>
                <Icon name="trash" size="sm" /> Purge all retained audio
              </button>
            </div>
            {narrow ? null : <p className="t-sec mut" style={{ marginTop: 12 }}>
              <span className="t-ovl">Export everything</span> is the promise, not a feature:
              patients, visits, appointments, bills, prescriptions and the access log, taken from
              here. Purging is the opposite promise — re-enabling never brings purged audio back,
              by design.
            </p>}


            {save.isError ? (
              <p className="t-sec red" style={{ marginTop: 10 }}>
                That did not save. Nothing was changed on the server — try again, or reload to see
                what is stored.
              </p>
            ) : null}
            {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
          </>
        ) : null}
      </div>

      <PatientDisclosureModal
        open={disclosure}
        onClose={() => setDisclosure(false)}
        retentionDays={shown?.retentionDays ?? 60}
      />

      <ExportEverything open={exporting} onClose={() => setExporting(false)} />

      <Modal
        open={explaining !== null}
        onClose={() => setExplaining(null)}
        title="Purge all retained audio"
        sub="irreversible, and not yours to press"
        width={460}
        foot={<button type="button" className="btn btn-outline" onClick={() => setExplaining(null)}>Close</button>}
      >
        <div style={{ marginTop: 12 }}>
          {explaining === 'purge' ? (
            <>
              <p className="t-sec sec">
                Purging withdraws every retained recording in this clinic at once: the objects are
                deleted and the rows stay as tombstones so a future training run skips them. It
                cannot be undone, and re-enabling contribution never brings purged audio back.
              </p>
              <p className="t-cap mut mt8">
                The endpoint behind it (<span className="w6">POST /audio-retention/withdraw-all</span>)
                is gated on <span className="w6">system:admin</span> — a platform permission, not a
                clinic one. Neither a manager nor the clinic’s owner holds it. MAIE support runs it
                on a written request, which is the point of the gate.
              </p>
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
